// File: src/components/pages/Dashboard.jsx
// Dashboard: shows today's bookings (auto-loaded) and a toggle to view tomorrow's.

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  LayoutDashboard,
  CalendarDays,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  Clock,
  User,
  RefreshCw,
  Calendar,
  ChevronLeft
} from 'lucide-react';
import { bookingAPI } from '../../services/api';
import '../../styles/unified.css';

// ─── helpers ────────────────────────────────────────────────────────────────

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 || 12;
  return `${display}:${m} ${ampm}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, mo, d] = dateStr.split('-');
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const statusColour = (status) => {
  switch (status) {
    case 'confirmed': return { bg: '#dcfce7', text: '#16a34a', label: 'Confirmed' };
    case 'pending':   return { bg: '#fef9c3', text: '#ca8a04', label: 'Pending'   };
    case 'cancelled': return { bg: '#fee2e2', text: '#dc2626', label: 'Cancelled' };
    default:          return { bg: '#f1f5f9', text: '#64748b', label: status      };
  }
};

// ─── BookingCard ─────────────────────────────────────────────────────────────

const BookingCard = ({ booking }) => {
  const [hovered, setHovered] = useState(false);
  const s = statusColour(booking.status);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        border: `1px solid ${hovered ? '#667eea' : '#e5e7eb'}`,
        borderRadius: '0.75rem',
        padding: '1.25rem 1.5rem',
        display: 'flex',
        gap: '1.25rem',
        alignItems: 'flex-start',
        boxShadow: hovered ? '0 4px 12px rgba(102,126,234,0.12)' : 'none',
        transition: 'box-shadow 0.2s, border-color 0.2s'
      }}
    >
      {/* Time column */}
      <div style={{ minWidth: '76px', textAlign: 'center' }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '0.6rem',
          padding: '0.6rem 0.5rem'
        }}>
          <Clock size={14} color="#fff" style={{ marginBottom: '0.2rem' }} />
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>
            {formatTime(booking.startTime)}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.68rem', margin: '0.15rem 0' }}>to</div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>
            {formatTime(booking.endTime)}
          </div>
        </div>
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name + status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.55rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <User size={15} color="#667eea" />
            <span style={{ fontWeight: 600, fontSize: '0.98rem', color: '#111827' }}>
              {booking.studentName || 'Unknown Student'}
            </span>
          </div>
          <span style={{
            background: s.bg, color: s.text,
            fontSize: '0.7rem', fontWeight: 700,
            padding: '0.15rem 0.55rem', borderRadius: '9999px',
            textTransform: 'capitalize'
          }}>
            {s.label}
          </span>
        </div>

        {/* Contact */}
        <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          {booking.studentPhone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Phone size={13} color="#6b7280" />
              <span style={{ fontSize: '0.82rem', color: '#374151' }}>{booking.studentPhone}</span>
            </div>
          )}
          {booking.studentEmail && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Mail size={13} color="#6b7280" />
              <span style={{ fontSize: '0.82rem', color: '#374151' }}>{booking.studentEmail}</span>
            </div>
          )}
        </div>

        {/* Pickup address */}
        {(booking.pickupAddress || booking.pickupPostcode) ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.35rem' }}>
            <MapPin size={13} color="#667eea" style={{ marginTop: '2px', flexShrink: 0 }} />
            <span style={{ fontSize: '0.82rem', color: '#4b5563' }}>
              {[booking.pickupAddress, booking.pickupPostcode].filter(Boolean).join(', ')}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <MapPin size={13} color="#d1d5db" />
            <span style={{ fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic' }}>
              No pickup address on file
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── BookingSection ───────────────────────────────────────────────────────────

const BookingSection = ({ title, dateStr, bookings, accentColor }) => (
  <div>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      marginBottom: '1rem', paddingBottom: '0.6rem',
      borderBottom: `2px solid ${accentColor}25`
    }}>
      <CalendarDays size={18} color={accentColor} />
      <div>
        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>{title}</h2>
        <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280' }}>{formatDate(dateStr)}</p>
      </div>
      <span style={{
        marginLeft: 'auto',
        background: accentColor + '15',
        color: accentColor,
        fontWeight: 700,
        fontSize: '0.78rem',
        padding: '0.2rem 0.65rem',
        borderRadius: '9999px'
      }}>
        {bookings.length} {bookings.length === 1 ? 'lesson' : 'lessons'}
      </span>
    </div>

    {bookings.length === 0 ? (
      <div style={{
        textAlign: 'center', padding: '2.5rem',
        background: '#f8fafc', borderRadius: '0.75rem',
        border: '1px dashed #e2e8f0'
      }}>
        <CalendarDays size={32} color="#d1d5db" style={{ marginBottom: '0.5rem' }} />
        <p style={{ color: '#9ca3af', margin: 0, fontSize: '0.88rem' }}>No lessons scheduled</p>
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {bookings.map(b => <BookingCard key={b.bookingId} booking={b} />)}
      </div>
    )}
  </div>
);

// ─── Dashboard ───────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [view, setView]       = useState('today'); // 'today' | 'tomorrow'

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await bookingAPI.getDashboardBookings(getAccessTokenSilently);
      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to load bookings.');
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load bookings.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="page-container">

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.75rem', paddingBottom: '1rem',
        borderBottom: '2px solid #e5e7eb', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <LayoutDashboard size={28} color="#667eea" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#111827' }}>
              Dashboard
            </h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
              Welcome back, {user?.name?.split(' ')[0] || 'Instructor'}
            </p>
          </div>
        </div>

        <button
          onClick={fetchBookings}
          disabled={loading}
          title="Refresh"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: '0.5rem', padding: '0.45rem 0.85rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            color: '#6b7280', fontSize: '0.85rem', fontWeight: 500,
            transition: 'all 0.15s', opacity: loading ? 0.6 : 1
          }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* ── Day toggle (only when data loaded) ── */}
      {!loading && !error && data && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
          {/* Today */}
          <button
            onClick={() => setView('today')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.5rem 1.1rem', borderRadius: '0.6rem',
              border: view === 'today' ? '2px solid #667eea' : '2px solid #e5e7eb',
              background: view === 'today'
                ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#fff',
              color: view === 'today' ? '#fff' : '#374151',
              fontWeight: 600, fontSize: '0.88rem',
              cursor: 'pointer', transition: 'all 0.15s'
            }}
          >
            <Calendar size={15} />
            Today
            {data.today.length > 0 && (
              <span style={{
                background: view === 'today' ? 'rgba(255,255,255,0.3)' : '#667eea18',
                color: view === 'today' ? '#fff' : '#667eea',
                borderRadius: '9999px', padding: '0.05rem 0.45rem',
                fontSize: '0.73rem', fontWeight: 700
              }}>
                {data.today.length}
              </span>
            )}
          </button>

          {/* Tomorrow */}
          <button
            onClick={() => setView('tomorrow')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.5rem 1.1rem', borderRadius: '0.6rem',
              border: view === 'tomorrow' ? '2px solid #764ba2' : '2px solid #e5e7eb',
              background: view === 'tomorrow'
                ? 'linear-gradient(135deg,#764ba2,#667eea)' : '#fff',
              color: view === 'tomorrow' ? '#fff' : '#374151',
              fontWeight: 600, fontSize: '0.88rem',
              cursor: 'pointer', transition: 'all 0.15s'
            }}
          >
            Tomorrow
            {data.tomorrow.length > 0 && (
              <span style={{
                background: view === 'tomorrow' ? 'rgba(255,255,255,0.3)' : '#764ba218',
                color: view === 'tomorrow' ? '#fff' : '#764ba2',
                borderRadius: '9999px', padding: '0.05rem 0.45rem',
                fontSize: '0.73rem', fontWeight: 700
              }}>
                {data.tomorrow.length}
              </span>
            )}
            <ChevronRight size={15} />
          </button>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }} />
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading bookings…</p>
        </div>
      )}

      {/* ── Error ── */}
      {!loading && error && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fca5a5',
          borderRadius: '0.75rem', padding: '1.25rem 1.5rem',
          color: '#dc2626', display: 'flex', alignItems: 'center',
          gap: '0.75rem', flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600 }}>Error:</span>
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={fetchBookings}
            style={{
              background: '#dc2626', color: '#fff', border: 'none',
              borderRadius: '0.4rem', padding: '0.35rem 0.85rem',
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Booking content ── */}
      {!loading && !error && data && (
        <>
          {view === 'today' && (
            <BookingSection
              title="Today's Lessons"
              dateStr={data.todayDate}
              bookings={data.today}
              accentColor="#667eea"
            />
          )}

          {view === 'tomorrow' && (
            <div>
              <button
                onClick={() => setView('today')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.3rem',
                  background: 'none', border: 'none', color: '#667eea',
                  fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                  padding: 0, marginBottom: '1rem'
                }}
              >
                <ChevronLeft size={15} /> Back to Today
              </button>
              <BookingSection
                title="Tomorrow's Lessons"
                dateStr={data.tomorrowDate}
                bookings={data.tomorrow}
                accentColor="#764ba2"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
